import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Landing.css';

function Landing() {
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const slideTimerRef = useRef(null);
  const navigate = useNavigate();

  const slides = [
    '/assets/bg1.jpg',
    '/assets/bg2.jpg',
    '/assets/bg3.jpg'
  ];

  // Preload images for smoother transitions
  useEffect(() => {
    slides.forEach(slide => {
      const img = new Image();
      img.src = slide;
    });
  }, []);

  useEffect(() => {
    // Clear any existing timers
    if (slideTimerRef.current) {
      clearInterval(slideTimerRef.current);
    }

    // Set up slide rotation
    slideTimerRef.current = setInterval(() => {
      changeSlide((currentSlide + 1) % slides.length);
    }, 8000);

    // Cleanup timer
    return () => {
      if (slideTimerRef.current) {
        clearInterval(slideTimerRef.current);
      }
    };
  }, [currentSlide, slides.length]);

  const changeSlide = (newIndex) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide(newIndex);
    
    // Reset transition state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 700);
  };

  const navigateWithLoader = (path) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate(path);
    }, 1000); // Reduced loading time for better UX
  };

  const handleRegister = () => navigateWithLoader("/register");
  const handleLogin = () => navigateWithLoader("/login");

  return (
    <div className="landing-page">
      {loading ? (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="spinner"></div>
            <p className="loading-text">Please wait...</p>
          </div>
        </div>
      ) : (
        <div className="landing-container">
          {/* Navigation Bar */}
          <nav className="landing-navbar">
            <div className="navbar-logo">
              <img src="assets/genie.png" alt="Genie Marketplace Logo" />
            </div>
            <div className="navbar-menu">
              <div className="navbar-menu-item language">EN</div>
              <div className="navbar-menu-item login" onClick={handleLogin}>Login</div>
              <button className="navbar-register-btn" onClick={handleRegister}>
                Register
              </button>
            </div>
          </nav>

          {/* Hero Section with Slider */}
          <header className="hero-section">
            <div className="hero-slider">
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
                  style={{ backgroundImage: `url(${slide})` }}
                />
              ))}
              
              {/* Slide Navigation Indicators */}
              <div className="slide-indicators">
                {slides.map((_, index) => (
                  <button 
                    key={index}
                    className={`slide-indicator ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => changeSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            
            <div className="hero-content">
              <h1>Make money by being a Vendor</h1>
              <p className="hero-subtitle">Join thousands of successful vendors on Genie Marketplace</p>
              <button className="cta-button" onClick={handleRegister}>
                Register as a vendor
              </button>
            </div>
          </header>

          {/* Benefits Section */}
          <section className="benefits-section">
            <div className="section-header">
              <h2>Genie Marketplace Vendor Benefits</h2>
              <p className="section-subtitle">Discover why thousands of vendors choose Genie Marketplace</p>
            </div>
            
            <div className="benefits-grid">
              <div className="benefit-card item1">
                <div className="benefit-icon">
                  <i className="fas fa-calendar" aria-hidden="true"></i>
                </div>
                <div className="benefit-content">
                  <h3>24 Hour Payouts</h3>
                  <p>Receive your earnings at the end of each week, with competitive fees.</p>
                </div>
              </div>
              
              <div className="benefit-card item2">
                <div className="benefit-icon">
                  <i className="fas fa-business-time" aria-hidden="true"></i>
                </div>
                <div className="benefit-content">
                  <h3>Flexible Subscription</h3>
                  <p>Flexible subscription plans to suit your specific business needs.</p>
                </div>
              </div>
              
              <div className="benefit-card item3">
                <div className="benefit-icon">
                  <i className="fas fa-headset" aria-hidden="true"></i>
                </div>
                <div className="benefit-content">
                  <h3>24/7 Support</h3>
                  <p>Comprehensive support for continuous business growth and success.</p>
                </div>
              </div>
              
              <div className="benefit-card item4">
                <div className="benefit-icon">
                  <i className="fas fa-eye" aria-hidden="true"></i>
                </div>
                <div className="benefit-content">
                  <h3>Visibility</h3>
                  <p>Increased visibility and access to a larger, targeted customer base.</p>
                </div>
              </div>
              
              <div className="benefit-card item5">
                <div className="benefit-icon">
                  <i className="fas fa-calendar-alt" aria-hidden="true"></i>
                </div>
                <div className="benefit-content">
                  <h3>Flexible Schedule</h3>
                  <p>Choose when and how often to earn money delivering for events.</p>
                </div>
              </div>
              
              <div className="benefit-card item6">
                <div className="benefit-icon">
                  <i className="fas fa-truck" aria-hidden="true"></i>
                </div>
                <div className="benefit-content">
                  <h3>Deliver Events Your Way</h3>
                  <p>Want to make money as a vendor driver, or get some exercise on your bike? You decide.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Call-to-Action Footer */}
          <section className="cta-section">
            <div className="cta-content">
              <h2>Ready to Start Your Vendor Journey?</h2>
              <p>Join Genie Marketplace today and grow your business.</p>
              <button className="cta-button" onClick={handleRegister}>
                Start Now
              </button>
            </div>
          </section>

          {/* Footer */}
          <footer className="landing-footer">
            <div className="footer-content">
              <p>&copy; {new Date().getFullYear()} Genie Marketplace. All rights reserved.</p>
            </div>
          </footer>
        </div>
      )}
    </div>
  );
}

export default Landing;